import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  IconButton,
  Grid,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TitleBar from '../../components/TitleBar/TitleBar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadIcon from '@mui/icons-material/Upload';
import CalculateIcon from '@mui/icons-material/Calculate';
import SyncIcon from '@mui/icons-material/Sync';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import {
  getUploadProgress,
  getComputeProgress,
  getUploadHistory,
  getComputeHistory,
  downloadUploadMetricsReport,
  downloadComputeMetricsReport,
  MetricsReportFormat,
} from '../../services/api';
import { formatDisplayDateTime } from '../../utils/dateUtils';

// ─── Shared interfaces ───────────────────────────────────────────────────────

interface SheetProgress {
  name: string;
  status: 'reading' | 'processing' | 'done' | 'error';
  started_at: string | null;
  completed_at: string | null;
  row_count: number;
  success_count: number;
  error_count: number;
  error: string | null;
  time_taken?: number | null;
}

interface UploadRowError {
  sheet: string;
  row_number: number | string;
  project?: string;
  error: string;
}

interface UploadProgressData {
  process_id?: number;
  status: 'in_progress' | 'completed' | 'failed' | 'not_found' | 'pending';
  total_sheets: number;
  current_sheet: string | null;
  current_sheet_status: 'reading' | 'processing' | 'done' | 'error' | null;
  current_sheet_total_rows: number;
  current_sheet_rows_processed: number;
  sheets: SheetProgress[];
  row_errors?: UploadRowError[];
  error: string | null;
  job_started_at?: string | null;
  job_completed_at?: string | null;
  time_taken?: number | null;
  file_name?: string;
  triggered_at?: string;
  triggered_by?: number;
}

interface ComputeProgressData {
  process_id?: number;
  status: 'in_progress' | 'completed' | 'failed' | 'not_found';
  phase: 'fetching_inputs' | 'computing';
  total_dimensions: number;
  dimensions_completed: number;
  current_dimension: string | null;
  current_dimension_status: 'fetching' | 'computing' | 'done' | 'error' | null;
  dimensions_summary: {
    [dim: string]: { status: string; time_taken?: number; rows_created?: number; error?: string };
  };
  dimension_errors: { [dim: string]: string };
  total_rows_created: number;
  error: string | null;
  elapsed_seconds: number;
  message?: string;
  file_name?: string;
  triggered_at?: string;
  triggered_by?: number;
  started_at?: string | null;
  completed_at?: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const isActiveStatus = (s?: string) => s === 'in_progress' || s === 'pending';

const formatTimestamp = (ts?: string | null): string =>
  ts ? formatDisplayDateTime(ts) : '—';

const uploadHasReportData = (data: UploadProgressData): boolean =>
  Boolean(data.process_id);

const computeHasReportData = (data: ComputeProgressData): boolean =>
  Boolean(data.process_id);

interface ReportDownloadButtonsProps {
  processId?: number;
  reportType: 'upload' | 'compute';
  disabled?: boolean;
}

const ReportDownloadButtons: React.FC<ReportDownloadButtonsProps> = ({
  processId,
  reportType,
  disabled = false,
}) => {
  const [downloadingFormat, setDownloadingFormat] = useState<MetricsReportFormat | null>(null);
  const [downloadError, setDownloadError] = useState<string>('');

  const handleDownload = async (format: MetricsReportFormat) => {
    if (!processId) return;
    setDownloadError('');
    setDownloadingFormat(format);
    try {
      if (reportType === 'upload') {
        await downloadUploadMetricsReport(processId, format);
      } else {
        await downloadComputeMetricsReport(processId, format);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to download report.';
      setDownloadError(message);
    } finally {
      setDownloadingFormat(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={
            downloadingFormat === 'xlsx'
              ? <CircularProgress size={14} />
              : <DownloadIcon fontSize="small" />
          }
          disabled={disabled || !processId || downloadingFormat !== null}
          onClick={() => handleDownload('xlsx')}
        >
          Download Excel
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={
            downloadingFormat === 'csv'
              ? <CircularProgress size={14} />
              : <DownloadIcon fontSize="small" />
          }
          disabled={disabled || !processId || downloadingFormat !== null}
          onClick={() => handleDownload('csv')}
        >
          Download CSV
        </Button>
      </Box>
      {downloadError && (
        <Alert severity="error" sx={{ py: 0, width: '100%' }}>{downloadError}</Alert>
      )}
    </Box>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────

const MetricsHistory: React.FC = () => {
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  // Upload state
  const [uploadProgress, setUploadProgress] = useState<UploadProgressData | null>(null);
  const [uploadHistory, setUploadHistory] = useState<UploadProgressData[]>([]);
  const [uploadDetailData, setUploadDetailData] = useState<UploadProgressData | null>(null);

  // Compute state
  const [computeProgress, setComputeProgress] = useState<ComputeProgressData | null>(null);
  const [computeHistory, setComputeHistory] = useState<ComputeProgressData[]>([]);
  const [computeDetailData, setComputeDetailData] = useState<ComputeProgressData | null>(null);

  // ── Data fetching ────────────────────────────────────────────────────────

  const fetchUploadHistory = useCallback(async () => {
    try {
      const res = await getUploadHistory();
      if (Array.isArray(res.data)) setUploadHistory(res.data);
    } catch (e) { console.warn('fetchUploadHistory: error', e); }
  }, []);

  const fetchComputeHistory = useCallback(async () => {
    try {
      const res = await getComputeHistory();
      if (Array.isArray(res.data)) setComputeHistory(res.data);
    } catch (e) { console.warn('fetchComputeHistory: error', e); }
  }, []);

  // ── Manual refresh ──────────────────────────────────────────────────────
  // Progress is updated only when the user explicitly clicks Refresh; there is
  // no automatic polling or WebSocket. Each click pulls the latest status over
  // plain HTTP.
  const [refreshing, setRefreshing] = useState(false);

  const handleManualRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const [uploadRes, computeRes] = await Promise.allSettled([
        getUploadProgress(),
        getComputeProgress(),
      ]);
      if (!mountedRef.current) return;

      if (uploadRes.status === 'fulfilled') {
        const ud: UploadProgressData = uploadRes.value.data;
        setUploadProgress(ud && ud.status !== 'not_found' ? ud : null);
      }
      if (computeRes.status === 'fulfilled') {
        const cd: ComputeProgressData = computeRes.value.data;
        setComputeProgress(cd && cd.status !== 'not_found' ? cd : null);
      }

      await Promise.all([fetchUploadHistory(), fetchComputeHistory()]);
    } catch (e) {
      console.warn('MetricsHistory: manual refresh failed', e);
    } finally {
      if (mountedRef.current) setRefreshing(false);
    }
  }, [fetchUploadHistory, fetchComputeHistory]);

  // ── On mount ─────────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const uploadRes = await getUploadProgress();
        if (cancelled) return;
        const ud: UploadProgressData = uploadRes.data;
        if (ud && ud.status !== 'not_found') setUploadProgress(ud);
      } catch (e) { console.warn('MetricsHistory: failed to fetch upload progress', e); }
      try {
        const computeRes = await getComputeProgress();
        if (cancelled) return;
        const cd: ComputeProgressData = computeRes.data;
        if (cd && cd.status !== 'not_found') setComputeProgress(cd);
      } catch (e) { console.warn('MetricsHistory: failed to fetch compute progress', e); }
      if (!cancelled) {
        await Promise.all([fetchUploadHistory(), fetchComputeHistory()]);
        setInitialLoading(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, [fetchUploadHistory, fetchComputeHistory]);

  // ── Progress helpers ─────────────────────────────────────────────────────

  const getUploadOverallProgress = (): number => {
    if (!uploadProgress || uploadProgress.total_sheets === 0) return 0;
    const doneSheets = (uploadProgress.sheets || []).filter(
      (s) => s.status === 'done' || s.status === 'error',
    ).length;
    const sheetProgress = (doneSheets / uploadProgress.total_sheets) * 100;
    if (uploadProgress.current_sheet && uploadProgress.current_sheet_total_rows > 0) {
      const rowFraction =
        uploadProgress.current_sheet_rows_processed / uploadProgress.current_sheet_total_rows;
      const perSheetWeight = 100 / uploadProgress.total_sheets;
      return Math.min(sheetProgress + rowFraction * perSheetWeight, 100);
    }
    return Math.min(sheetProgress, 100);
  };

  const getComputeOverallProgress = (): number => {
    if (!computeProgress || computeProgress.total_dimensions === 0) return 0;
    return Math.min(
      (computeProgress.dimensions_completed / computeProgress.total_dimensions) * 100,
      100,
    );
  };

  // ── Tab panels ───────────────────────────────────────────────────────────

  const renderUploadInProgress = () => {
    if (!uploadProgress || !isActiveStatus(uploadProgress.status)) return null;
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <SyncIcon fontSize="small" /> Upload In Progress
          </Typography>
          <Chip
            label={uploadProgress.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
            size="small"
            color="info"
          />
        </Box>

        {uploadProgress.file_name && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            File: <strong>{uploadProgress.file_name}</strong>
          </Typography>
        )}
        {uploadProgress.job_started_at && (
          <Typography variant="caption" color="text.secondary" display="block">
            Started: {formatTimestamp(uploadProgress.job_started_at)}
          </Typography>
        )}

        <Box sx={{ mt: 1 }}>
          {uploadProgress.total_sheets > 0 ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">
                  Sheets: {(uploadProgress.sheets || []).filter((s) => s.status === 'done' || s.status === 'error').length} / {uploadProgress.total_sheets}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(getUploadOverallProgress())}%
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={getUploadOverallProgress()} sx={{ height: 8, borderRadius: 4 }} />
              {uploadProgress.current_sheet && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Current: {uploadProgress.current_sheet} — {uploadProgress.current_sheet_status?.replace('_', ' ')}
                  {uploadProgress.current_sheet_total_rows > 0 &&
                    ` (${uploadProgress.current_sheet_rows_processed}/${uploadProgress.current_sheet_total_rows} rows)`}
                </Typography>
              )}
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Starting...</Typography>
            </Box>
          )}
        </Box>

        {uploadProgress.sheets && uploadProgress.sheets.length > 0 && (
          <TableContainer sx={{ mt: 1.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Sheet</strong></TableCell>
                  <TableCell align="right"><strong>Rows</strong></TableCell>
                  <TableCell align="right"><strong>Success</strong></TableCell>
                  <TableCell align="right"><strong>Errors</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uploadProgress.sheets.map((sheet) => (
                  <TableRow key={sheet.name}>
                    <TableCell>{sheet.name}</TableCell>
                    <TableCell align="right">{sheet.row_count}</TableCell>
                    <TableCell align="right">{sheet.success_count}</TableCell>
                    <TableCell align="right" sx={{ color: sheet.error_count > 0 ? 'error.main' : 'inherit' }}>
                      {sheet.error_count}
                    </TableCell>
                    <TableCell>
                      <Chip label={sheet.status} size="small"
                        color={sheet.status === 'done' ? 'success' : sheet.status === 'error' ? 'error' : 'info'}
                        variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {uploadProgress.error && (
          <Alert severity="error" sx={{ mt: 1, py: 0 }}>{uploadProgress.error}</Alert>
        )}

        {uploadProgress.process_id &&
          !isActiveStatus(uploadProgress.status) &&
          uploadHasReportData(uploadProgress) && (
          <Box sx={{ mt: 2 }}>
            <ReportDownloadButtons processId={uploadProgress.process_id} reportType="upload" />
          </Box>
        )}
      </Paper>
    );
  };

  const renderUploadHistory = () => (
    <>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <UploadIcon fontSize="small" />
        Upload History
      </Typography>
      {uploadHistory.length > 0 ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>File</strong></TableCell>
                <TableCell><strong>Started</strong></TableCell>
                <TableCell><strong>Completed</strong></TableCell>
                <TableCell align="right"><strong>Elapsed (s)</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Sheets</strong></TableCell>
                <TableCell align="right"><strong>Rows</strong></TableCell>
                <TableCell align="right"><strong>Errors</strong></TableCell>
                <TableCell align="center"><strong>Details</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {uploadHistory.map((proc) => {
                const sheets = proc.sheets || [];
                const totalRows = sheets.reduce((sum: number, s: SheetProgress) => sum + (s.row_count || 0), 0);
                const totalErrors = sheets.reduce((sum: number, s: SheetProgress) => sum + (s.error_count || 0), 0);
                return (
                  <TableRow key={proc.process_id}>
                    <TableCell>{proc.file_name || '—'}</TableCell>
                    <TableCell>{formatTimestamp(proc.job_started_at || proc.triggered_at)}</TableCell>
                    <TableCell>{formatTimestamp(proc.job_completed_at)}</TableCell>
                    <TableCell align="right">{proc.time_taken != null ? proc.time_taken : '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={proc.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
                        size="small"
                        color={proc.status === 'completed' ? 'success' : proc.status === 'failed' ? 'error' : 'info'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{sheets.length || '—'}</TableCell>
                    <TableCell align="right">{totalRows}</TableCell>
                    <TableCell align="right" sx={{ color: totalErrors > 0 ? 'error.main' : 'inherit' }}>
                      {totalErrors}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => setUploadDetailData(proc)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary">No upload history yet.</Typography>
      )}
    </>
  );

  const renderComputeInProgress = () => {
    if (!computeProgress || !isActiveStatus(computeProgress.status)) return null;
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <SyncIcon fontSize="small" /> Computation In Progress
          </Typography>
          <Chip
            label={computeProgress.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
            size="small"
            color="info"
          />
        </Box>

        {computeProgress.triggered_at && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Started: {formatTimestamp(computeProgress.triggered_at)}
          </Typography>
        )}

        <Box sx={{ mt: 1 }}>
          {computeProgress.phase && (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Phase: <strong>{computeProgress.phase.replace('_', ' ')}</strong>
            </Typography>
          )}
          {computeProgress.total_dimensions > 0 ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">
                  Dimensions: {computeProgress.dimensions_completed} / {computeProgress.total_dimensions}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(getComputeOverallProgress())}%
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={getComputeOverallProgress()} sx={{ height: 8, borderRadius: 4 }} />
              {computeProgress.current_dimension && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Current: {computeProgress.current_dimension} — {computeProgress.current_dimension_status?.replace('_', ' ')}
                </Typography>
              )}
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Starting...</Typography>
            </Box>
          )}
        </Box>

        {computeProgress.error && (
          <Alert severity="error" sx={{ mt: 1, py: 0 }}>{computeProgress.error}</Alert>
        )}

        {computeProgress.process_id &&
          !isActiveStatus(computeProgress.status) &&
          computeHasReportData(computeProgress) && (
          <Box sx={{ mt: 2 }}>
            <ReportDownloadButtons processId={computeProgress.process_id} reportType="compute" />
          </Box>
        )}
      </Paper>
    );
  };

  const renderComputeHistory = () => (
    <>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CalculateIcon fontSize="small" />
        Compute History
      </Typography>
      {computeHistory.length > 0 ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Started</strong></TableCell>
                <TableCell><strong>Completed</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Dimensions</strong></TableCell>
                <TableCell align="right"><strong>Rows Created</strong></TableCell>
                <TableCell align="center"><strong>Details</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {computeHistory.map((proc) => {
                const dimSummary = proc.dimensions_summary || {};
                const dimCount = Object.keys(dimSummary).length;
                return (
                  <TableRow key={proc.process_id}>
                    <TableCell>{proc.file_name || '—'}</TableCell>
                    <TableCell>{formatTimestamp(proc.started_at || proc.triggered_at)}</TableCell>
                    <TableCell>{formatTimestamp(proc.completed_at)}</TableCell>
                    <TableCell>
                      <Chip
                        label={proc.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
                        size="small"
                        color={proc.status === 'completed' ? 'success' : proc.status === 'failed' ? 'error' : 'info'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{dimCount || '—'}</TableCell>
                    <TableCell align="right">{proc.total_rows_created ?? '—'}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => setComputeDetailData(proc)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary">No compute history yet.</Typography>
      )}
    </>
  );

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <TitleBar />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton
            onClick={() => navigate('/view-metrics')}
            sx={{
              border: '1px solid #ccc',
              borderRadius: 2,
              p: 1,
              backgroundColor: '#f5f5f5',
              '&:hover': { backgroundColor: '#e0e0e0' },
              mr: 2,
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight="bold">
            Metrics Load &amp; Compute History
          </Typography>
          <Button
            variant="outlined"
            onClick={handleManualRefresh}
            disabled={refreshing}
            startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
            sx={{ ml: 'auto' }}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
        </Box>

        <Paper elevation={2} sx={{ mt: 2 }}>
          <Tabs
            value={tabIndex}
            onChange={(_, v) => setTabIndex(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            <Tab label="Metric Inputs" />
            <Tab label="Computed Metrics" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {initialLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
                <CircularProgress size={24} />
                <Typography color="text.secondary">Loading...</Typography>
              </Box>
            ) : (
              <>
                {tabIndex === 0 && (
                  <>
                    {renderUploadInProgress()}
                    {renderUploadHistory()}
                  </>
                )}
                {tabIndex === 1 && (
                  <>
                    {renderComputeInProgress()}
                    {renderComputeHistory()}
                  </>
                )}
              </>
            )}
          </Box>
        </Paper>
      </Container>

      {/* Upload Detail Dialog */}
      <Dialog
        open={!!uploadDetailData}
        maxWidth="md"
        fullWidth
        onClose={() => setUploadDetailData(null)}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadIcon fontSize="small" />
          Upload Details — {uploadDetailData?.file_name || 'Unknown'}
        </DialogTitle>
        <DialogContent dividers>
          {uploadDetailData && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box>
                    <Chip
                      label={uploadDetailData.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
                      size="small"
                      color={uploadDetailData.status === 'completed' ? 'success' : uploadDetailData.status === 'failed' ? 'error' : 'info'}
                      variant="outlined"
                    />
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Started</Typography>
                  <Typography variant="body2">
                    {formatTimestamp(uploadDetailData.job_started_at || uploadDetailData.triggered_at)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Completed</Typography>
                  <Typography variant="body2">
                    {formatTimestamp(uploadDetailData.job_completed_at)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Total Sheets</Typography>
                  <Typography variant="body2">{uploadDetailData.total_sheets || 0}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Elapsed (s)</Typography>
                  <Typography variant="body2">{uploadDetailData.time_taken != null ? uploadDetailData.time_taken : '—'}</Typography>
                </Grid>
              </Grid>

              {uploadDetailData.error && (
                <Alert severity="error" sx={{ mb: 2 }}>{uploadDetailData.error}</Alert>
              )}

              {(uploadDetailData.sheets || []).length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Sheet</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell align="right"><strong>Total Rows</strong></TableCell>
                        <TableCell align="right"><strong>Success</strong></TableCell>
                        <TableCell align="right"><strong>Errors</strong></TableCell>
                        <TableCell><strong>Started</strong></TableCell>
                        <TableCell><strong>Completed</strong></TableCell>
                        <TableCell align="right"><strong>Elapsed (s)</strong></TableCell>
                        <TableCell><strong>Error Message</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {uploadDetailData.sheets.map((sheet) => (
                        <TableRow key={sheet.name}>
                          <TableCell>{sheet.name}</TableCell>
                          <TableCell>
                            <Chip
                              label={sheet.status?.toUpperCase() || '—'}
                              size="small"
                              color={sheet.status === 'done' ? 'success' : sheet.status === 'error' ? 'error' : 'info'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">{sheet.row_count}</TableCell>
                          <TableCell align="right">{sheet.success_count}</TableCell>
                          <TableCell align="right" sx={{ color: sheet.error_count > 0 ? 'error.main' : 'inherit' }}>
                            {sheet.error_count}
                          </TableCell>
                          <TableCell>{formatTimestamp(sheet.started_at)}</TableCell>
                          <TableCell>{formatTimestamp(sheet.completed_at)}</TableCell>
                          <TableCell align="right">{sheet.time_taken != null ? sheet.time_taken : '—'}</TableCell>
                          <TableCell sx={{ color: sheet.error ? 'error.main' : 'inherit' }}>
                            {sheet.error || ''}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {uploadDetailData.process_id && uploadHasReportData(uploadDetailData) && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Download Report
                  </Typography>
                  <ReportDownloadButtons processId={uploadDetailData.process_id} reportType="upload" />
                </Box>
              )}

              {(uploadDetailData.row_errors || []).length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Row Errors ({uploadDetailData.row_errors!.length})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Excel row numbers include the header row (row 1). Use these to locate and fix rows in your file.
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Sheet</strong></TableCell>
                          <TableCell align="right"><strong>Excel Row</strong></TableCell>
                          <TableCell><strong>Project</strong></TableCell>
                          <TableCell><strong>Error</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {uploadDetailData.row_errors!.map((rowError, idx) => (
                          <TableRow key={`${rowError.sheet}-${rowError.row_number}-${idx}`}>
                            <TableCell>{rowError.sheet || '—'}</TableCell>
                            <TableCell align="right">{rowError.row_number ?? '—'}</TableCell>
                            <TableCell>{rowError.project || '—'}</TableCell>
                            <TableCell sx={{ color: 'error.main' }}>{rowError.error}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDetailData(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Compute Detail Dialog */}
      <Dialog
        open={!!computeDetailData}
        maxWidth="md"
        fullWidth
        onClose={() => setComputeDetailData(null)}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalculateIcon fontSize="small" />
          Compute Details — {computeDetailData?.file_name || 'Unknown'}
        </DialogTitle>
        <DialogContent dividers>
          {computeDetailData && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box>
                    <Chip
                      label={computeDetailData.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
                      size="small"
                      color={computeDetailData.status === 'completed' ? 'success' : computeDetailData.status === 'failed' ? 'error' : 'info'}
                      variant="outlined"
                    />
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Started</Typography>
                  <Typography variant="body2">
                    {formatTimestamp(computeDetailData.started_at || computeDetailData.triggered_at)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Completed</Typography>
                  <Typography variant="body2">
                    {formatTimestamp(computeDetailData.completed_at)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Rows Created</Typography>
                  <Typography variant="body2">{computeDetailData.total_rows_created ?? 0}</Typography>
                </Grid>
              </Grid>

              {computeDetailData.error && (
                <Alert severity="error" sx={{ mb: 2 }}>{computeDetailData.error}</Alert>
              )}

              {computeDetailData.dimensions_summary &&
                Object.keys(computeDetailData.dimensions_summary).length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Dimension</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell align="right"><strong>Time (s)</strong></TableCell>
                        <TableCell align="right"><strong>Rows</strong></TableCell>
                        <TableCell><strong>Error</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(computeDetailData.dimensions_summary).map(([dim, stats]) => (
                        <TableRow key={dim}>
                          <TableCell>{dim}</TableCell>
                          <TableCell>
                            <Chip
                              label={stats.status?.toUpperCase() || '—'}
                              size="small"
                              color={stats.status === 'error' ? 'error' : 'success'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">{stats.time_taken ? Math.round(stats.time_taken) : '—'}</TableCell>
                          <TableCell align="right">{stats.rows_created ?? '—'}</TableCell>
                          <TableCell sx={{ color: 'error.main' }}>{stats.error || ''}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {computeDetailData.process_id && computeHasReportData(computeDetailData) && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Download Report
                  </Typography>
                  <ReportDownloadButtons processId={computeDetailData.process_id} reportType="compute" />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComputeDetailData(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MetricsHistory;
